<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { AudioLines, Bot, MessageSquareText, RefreshCw, Settings, X } from "lucide-vue-next";
import { api } from "./services/api";
import { clientConfig, saveClientConfig } from "./state/config";
import type { AgentSummary, Health } from "./types";
import ChatView from "./views/ChatView.vue";
import RealtimeView from "./views/RealtimeView.vue";

const activeMode = ref<"chat" | "realtime">("chat");
const agents = ref<AgentSummary[]>([]);
const health = ref<Health>();
const loading = ref(false);
const error = ref("");
const settingsOpen = ref(false);
const baseUrl = ref(clientConfig.baseUrl);
const clientKey = ref(clientConfig.clientKey);
let timer: number | undefined;

const ready = computed(() => health.value?.status === "ready");

async function refresh(): Promise<void> {
  loading.value = true;
  error.value = "";
  const [healthResult, agentResult] = await Promise.allSettled([api.health(), api.agents()]);
  if (healthResult.status === "fulfilled") health.value = healthResult.value;
  else error.value = healthResult.reason instanceof Error ? healthResult.reason.message : "服务连接失败";
  if (agentResult.status === "fulfilled") agents.value = agentResult.value;
  else if (!error.value) error.value = agentResult.reason instanceof Error ? agentResult.reason.message : "智能体加载失败";
  loading.value = false;
}

function openSettings(): void {
  baseUrl.value = clientConfig.baseUrl;
  clientKey.value = clientConfig.clientKey;
  settingsOpen.value = true;
}

function submitSettings(): void {
  saveClientConfig({ baseUrl: baseUrl.value, clientKey: clientKey.value });
  settingsOpen.value = false;
  void refresh();
}

onMounted(() => {
  void refresh();
  timer = window.setInterval(() => void refresh(), 15_000);
});
onBeforeUnmount(() => timer && window.clearInterval(timer));
</script>

<template>
  <div class="client-shell">
    <header class="client-header">
      <div class="client-brand">
        <span class="client-brand-mark"><Bot :size="20" /></span>
        <div><strong>AI 交互终端</strong><small>{{ ready ? "服务已连接" : "服务未就绪" }}</small></div>
      </div>
      <div class="client-header-actions">
        <span v-if="error" class="inline-error">{{ error }}</span>
        <button class="icon-button" type="button" title="刷新" :disabled="loading" @click="refresh"><RefreshCw :size="18" :class="{ spinning: loading }" /></button>
        <button class="icon-button" type="button" title="连接设置" @click="openSettings"><Settings :size="18" /></button>
      </div>
    </header>

    <nav class="client-mode-tabs" aria-label="交互方式">
      <button type="button" :class="{ active: activeMode === 'chat' }" @click="activeMode = 'chat'"><MessageSquareText :size="17" />文字对话</button>
      <button type="button" :class="{ active: activeMode === 'realtime' }" @click="activeMode = 'realtime'"><AudioLines :size="17" />实时语音</button>
    </nav>

    <main class="client-main">
      <ChatView v-if="activeMode === 'chat'" :agents="agents" />
      <RealtimeView v-else :agents="agents" />
    </main>

    <div v-if="settingsOpen" class="modal-backdrop" @click.self="settingsOpen = false">
      <section class="modal-panel" role="dialog" aria-modal="true" aria-labelledby="client-settings-title">
        <div class="modal-header">
          <div><span class="eyebrow">终端连接</span><h2 id="client-settings-title">服务设置</h2></div>
          <button class="icon-button" type="button" title="关闭" @click="settingsOpen = false"><X :size="18" /></button>
        </div>
        <form class="form-stack" @submit.prevent="submitSettings">
          <label><span>服务地址</span><input v-model="baseUrl" placeholder="留空使用当前地址" /></label>
          <label><span>客户端密钥</span><input v-model="clientKey" type="password" autocomplete="off" /></label>
          <div class="modal-actions">
            <button class="button secondary" type="button" @click="settingsOpen = false">取消</button>
            <button class="button primary" type="submit">保存连接</button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>
